import Skeleton from "@material-ui/lab/Skeleton";
import {
  makeStyles,
  Typography,
  Popover,
  Grid,
  Button,
  Divider
} from "@material-ui/core";
import React, { useEffect, useRef, useState, useImperativeHandle } from "react";
import OptimizedTooltip from "../common/OptimizedTooltip";
import { getDateValue } from "../../utils/helpers";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import locale from "date-fns/locale/en-US";
import useProductTour, {
  PRODUCT_TOUR_STEP_IDS
} from "../../states/tour/tours/product";
import clsx from "clsx";

const useStyles = makeStyles(theme => ({
  date: {
    display: "inline-block",
    color: "#808080",
    fontSize: theme.typography.pxToRem(14),
    lineHeight: theme.typography.pxToRem(14),
    fontWeight: "normal",
    cursor: "pointer",
    padding: theme.spacing(0.55, 1),
    marginLeft: -4,
    "&:hover": {
      borderRadius: 4,
      backgroundColor: "#F4F4F4"
    }
  },
  pickerWrapper: {
    padding: 0,
    "& .MuiPickersDay-day": {
      width: 40,
      height: 40,
      margin: theme.spacing(0.375, 0.5)
    },
    "& .MuiPickersDay-dayDisabled": {
      color: "#C2C3C7",
      pointerEvents: "auto",
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.08)"
      }
    },
    "& .MuiPickersBasePicker-pickerView": {
      minHeight: 338,
      minWidth: 332,
      justifyContent: "flex-start"
    },
    "& .MuiPickersCalendarHeader-switchHeader": {
      marginBottom: theme.spacing(2.375),
      marginTop: 0
    },
    "& .MuiPickersCalendar-transitionContainer": {
      marginTop: theme.spacing(2.375)
    },
    "& .MuiPickersCalendarHeader-transitionContainer p, & .MuiPickersCalendarHeader-dayLabel":
      {
        color: "#606165",
        opacity: 1
      }
  },
  contentWrapper: {
    padding: theme.spacing(1, 2, 1.5)
  },
  divider: {
    margin: theme.spacing(0, -2, 1.5, -2)
  },
  saveBtn: {
    textTransform: "none",
    marginLeft: theme.spacing(1),
    padding: theme.spacing(1, 2)
  },
  cancelBtn: {
    color: "#606165",
    padding: theme.spacing(1, 2)
  }
}));

const dateFns = new DateFnsUtils();

const HeaderDate = (props, ref) => {
  const { projectDate, updateProject, isNotAllowedToEdit } = props;

  // General
  const classes = useStyles();

  // Custom
  const productTour = useProductTour();

  // States
  const [pickerValue, setPickerValue] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEntered, setIsEntered] = useState(false);

  // Variables
  const isProductTourPartProjectInitializeStepSetDate =
    productTour.isTourStepId(
      PRODUCT_TOUR_STEP_IDS.PART_INITIALIZE_NEW_PROJECT_STEP_SET_DATE
    );

  // Refs
  const containerRef = useRef(null);

  // Variables
  const date = getDateValue(projectDate);

  // Callbacks
  const onDateClick = () => {
    if (!pickerOpen) {
      setPickerOpen(true);
    }
  };

  const onClose = () => {
    setPickerOpen(false);
    setTimeout(() => {
      setPickerValue(projectDate ? new Date(projectDate) : new Date());
    }, 100);
  };

  const onSaveClick = () => {
    if (isProductTourPartProjectInitializeStepSetDate) {
      productTour.hidePulsingBorder();
      productTour.goToNext();
    }
    setSaving(true);
    updateProject(
      {
        date: dateFns.format(new Date(getDateValue(pickerValue)), "dd/MM/yyyy")
      },
      () => {
        setPickerOpen(false);
        setTimeout(() => {
          setSaving(false);
        }, 150);
      },
      () => {
        setSaving(false);
      }
    );
  };

  // Imperative handle
  useImperativeHandle(ref, () => ({
    open: () => {
      setPickerOpen(true);
    }
  }));

  // Effects
  useEffect(() => {
    if (projectDate && pickerValue === null) {
      setPickerValue(projectDate ? new Date(projectDate) : new Date());
    }
  }, [projectDate]);

  if (!date) {
    return (
      <Skeleton width="100%" style={{ backgroundColor: "rgba(0,0,0,.15)" }} />
    );
  }

  return (
    <div ref={containerRef}>
      <div>
        <OptimizedTooltip placement="bottom" title="Project date">
          <Typography
            variant="h5"
            className={classes.date}
            noWrap
            data-id="projectDetails-headerDate"
            onClick={onDateClick}
            style={{
              cursor:
                (productTour.hasActiveTourCurrently() &&
                  !isProductTourPartProjectInitializeStepSetDate) ||
                isNotAllowedToEdit
                  ? "default"
                  : "pointer",
              pointerEvents:
                (productTour.hasActiveTourCurrently() &&
                  !isProductTourPartProjectInitializeStepSetDate) ||
                isNotAllowedToEdit
                  ? "none"
                  : "auto"
            }}
          >
            {new Date(date).toLocaleString("en-US", {
              weekday: "long",
              month: "short",
              day: "2-digit"
            })}
          </Typography>
        </OptimizedTooltip>
      </div>
      <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
        <Popover
          open={pickerOpen}
          anchorEl={containerRef.current}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left"
          }}
          onClose={!productTour.hasActiveTourCurrently() && onClose}
          classes={{ paper: classes.pickerWrapper }}
          onEntered={() => setIsEntered(true)}
          onExited={() => setIsEntered(false)}
        >
          <div
            className={clsx(classes.contentWrapper, isEntered && "entered")}
            id="project_date_picker_wrapper"
          >
            <DatePicker
              open={pickerOpen}
              disableToolbar
              disablePast
              variant="static"
              format="yyyy/MM/dd"
              value={pickerValue}
              onChange={value => {
                if (isProductTourPartProjectInitializeStepSetDate) {
                  productTour.showPulsingBorder();
                }
                setPickerValue(value);
              }}
              onClose={() => {
                setPickerOpen(false);
              }}
            />
            <Divider variant="fullWidth" className={classes.divider} />
            <Grid
              container
              alignItems="flex-end"
              alignContent="center"
              justify="flex-end"
            >
              <Grid item>
                <Button
                  className={classes.cancelBtn}
                  onClick={onClose}
                  disabled={
                    saving || isProductTourPartProjectInitializeStepSetDate
                  }
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <Button
                  onClick={onSaveClick}
                  color="primary"
                  className={classes.saveBtn}
                  variant="contained"
                  disableElevation
                  disabled={saving}
                  id="project_date_save_button"
                >
                  {saving ? "Saving" : "Save"}
                </Button>
              </Grid>
            </Grid>
          </div>
        </Popover>
      </MuiPickersUtilsProvider>
    </div>
  );
};

export default React.forwardRef(HeaderDate);
